import type { ComponentProps, ReactNode } from 'react'
import React from 'react'

import type { Button, ModalContextType } from '@fourthwall/components'
import { ButtonClean, Icon } from '@fourthwall/components'
import { TwitchIcon, YoutubeIcon } from '@fourthwall/icons'
import { CreateProductModal } from '@modules/Dashboard/Product/CreateProduct/CreateProductModal'
import { checklistDataLayer } from '@utils/gtm/checklistDataLayer'
import { routing } from '@utils/routing'

import { OnboardingProductsSlider } from '../../../modules/ProductsSlider'
import connectTwitchGiftingImage from './assets/connect-twitch-gifting.png'
import connectYoutubeProductShelfImage from './assets/connect-youtube-product-shelf.png'
import customizeThemeImage from './assets/customize-theme.png'
import type {
  OnboardingSummaryChecklistItem,
  OnboardingSummaryChecklistItemKnownType,
  OnboardingSummaryChecklistItemType,
} from './types'

export const sortChecklistItems = (
  a: OnboardingSummaryChecklistItem,
  b: OnboardingSummaryChecklistItem,
): number => {
  const ORDER: string[] = (
    [
      'CreateNewProduct',
      'ConnectYoutubeProductShelf',
      'ConnectTwitchGifting',
      'CustomiseWebsiteLook',
    ] satisfies OnboardingSummaryChecklistItemKnownType[]
  ).reverse()

  return ORDER.indexOf(b.action.type) - ORDER.indexOf(a.action.type)
}

export const getShopChecklistItemsConfig = ({
  openModal,
  skipItem,
  shouldOnboarding,
  themeEditorPath,
}: {
  openModal: ModalContextType['open']
  skipItem: (actionType: OnboardingSummaryChecklistItemType) => Promise<void>
  shouldOnboarding: boolean
  themeEditorPath: string
}): Record<
  OnboardingSummaryChecklistItemType,
  {
    title: string
    description?: ReactNode
    imageUrl?: string
    primaryButton: ComponentProps<typeof Button>
    secondaryButton?: ComponentProps<typeof Button>
    completedButton?: ComponentProps<typeof ButtonClean>
    node?: ReactNode
    onSkipConfirm?: () => void
  }
> => ({
  CreateNewProduct: {
    title: 'Create your first product',
    primaryButton: {
      label: 'Create product',
      onClick: () => {
        openModal(CreateProductModal)
        checklistDataLayer('checklistStep', 'Home', 'Launch', 'Create your first product')
      },
    },
    secondaryButton: {
      label: 'Browse all',
      href: routing.productCatalog.self,
      target: '_blank',
    },
    completedButton: {
      label: 'Create another',
      onClick: () => {
        openModal(CreateProductModal)
      },
    },
    node: <OnboardingProductsSlider />,
    onSkipConfirm: async () => {
      await skipItem('CreateNewProduct')
      checklistDataLayer('skipChecklistStepConfirm', 'Home', 'Launch', 'Create your first product')
    },
  },
  ConnectYoutubeProductShelf: {
    title: 'Set up YouTube Product Shelf',
    description: (
      <>
        Feature products from your shop on your YouTube channels.{' '}
        <ButtonClean
          appearance="secondary"
          label="Learn more"
          href={routing.external.helpCenter.youtubeProductShelf}
          target="_blank"
        />
      </>
    ),
    imageUrl: connectYoutubeProductShelfImage,
    primaryButton: {
      label: 'Connect YouTube',
      appearance: 'secondary',
      leftIcon: <Icon component={YoutubeIcon} height={20} />,
      iconColor: '#FF0000',
      to: routing.apps.youTubeProductShelf,
      onClick: () => {
        checklistDataLayer('checklistStep', 'Home', 'Launch', 'Set up YouTube Product Shelf')
      },
    },
    onSkipConfirm: async () => {
      await skipItem('ConnectYoutubeProductShelf')
      checklistDataLayer(
        'skipChecklistStepConfirm',
        'Home',
        'Launch',
        'Set up YouTube Product Shelf',
      )
    },
  },
  ConnectTwitchGifting: {
    title: 'Set up Twitch Gifting',
    description: (
      <>
        Allow your viewers to gift products and memberships for chat when you’re live.{' '}
        <ButtonClean
          appearance="secondary"
          label="Learn more"
          href={routing.external.helpCenter.twitchGifting}
          target="_blank"
        />
      </>
    ),
    imageUrl: connectTwitchGiftingImage,
    primaryButton: {
      label: 'Connect Twitch',
      appearance: 'secondary',
      leftIcon: <Icon component={TwitchIcon} height={20} />,
      iconColor: '#9146FF',
      to: routing.apps.twitch,
      onClick: () => {
        checklistDataLayer('checklistStep', 'Home', 'Launch', 'Set up Twitch Gifting')
      },
    },
    onSkipConfirm: async () => {
      await skipItem('ConnectTwitchGifting')
      checklistDataLayer('skipChecklistStepConfirm', 'Home', 'Launch', 'Set up Twitch Gifting')
    },
  },
  CustomiseWebsiteLook: {
    title: 'Customize the look of your site',
    description:
      'Select your theme and make it yours. Change colors, add logo, set up your homepage sections, and much more.',
    imageUrl: customizeThemeImage,
    primaryButton: {
      label: 'Customize site',
      to: shouldOnboarding ? undefined : themeEditorPath,
      href: shouldOnboarding ? routing.onboarding : undefined,
      onClick: () => {
        checklistDataLayer('checklistStep', 'Home', 'Launch', 'Customize the look of your site')
      },
    },
    completedButton: {
      label: 'Edit',
      to: shouldOnboarding ? undefined : themeEditorPath,
      href: shouldOnboarding ? routing.onboarding : undefined,
    },
    onSkipConfirm: async () => {
      await skipItem('CustomiseWebsiteLook')
      checklistDataLayer(
        'skipChecklistStepConfirm',
        'Home',
        'Launch',
        'Customize the look of your site',
      )
    },
  },
})
